import type { GuestAcquisitionViewability } from "./guestAcquisitionObservation";

export const GUEST_ACQUISITION_CTA_SELECTOR = "[data-guest-acquisition-cta]";

type IntersectionObserverFactory = (
  callback: IntersectionObserverCallback,
  options: IntersectionObserverInit,
) => Pick<IntersectionObserver, "disconnect" | "observe">;

export interface BrowserCtaViewabilityDependencies {
  resolveTarget: () => Element | null;
  createObserver?: IntersectionObserverFactory;
}

const createNativeObserver: IntersectionObserverFactory = (callback, options) =>
  new IntersectionObserver(callback, options);

/**
 * Keeps browser geometry out of the Guest Acquisition feature interface. The
 * observation module receives only the bounded question it owns: whether the
 * CTA is currently at least half viewable.
 */
export const createBrowserCtaViewability = (
  dependencies: BrowserCtaViewabilityDependencies = {
    resolveTarget: () => document.querySelector(GUEST_ACQUISITION_CTA_SELECTOR),
  },
): GuestAcquisitionViewability => ({
  observe(onViewabilityChanged) {
    try {
      const target = dependencies.resolveTarget();
      const createObserver =
        dependencies.createObserver ??
        (typeof IntersectionObserver === "undefined"
          ? undefined
          : createNativeObserver);
      if (!target || !createObserver) return () => {};

      const observer = createObserver(
        (entries) => {
          try {
            const entry = entries.find(
              (candidate) => candidate.target === target,
            );
            if (!entry) return;
            onViewabilityChanged(
              entry.isIntersecting && entry.intersectionRatio >= 0.5,
            );
          } catch {
            // Browser observation is optional and cannot affect the task.
          }
        },
        { threshold: [0, 0.5, 1] },
      );
      observer.observe(target);
      return () => {
        try {
          observer.disconnect();
        } catch {
          // Browser observation is optional and cannot affect the task.
        }
      };
    } catch {
      return () => {};
    }
  },
});
